/**
 * Iter 67df — "Popular routes from this airport/port/city" widget.
 *
 * Rendered on `/car-rental/:slug` landing pages (LocationLandingPage).
 * Pulls from `/api/locations/popular-routes/{slug}` which ranks
 * destinations by cache hit-count and falls back to nearest
 * same-country locations on a cold cache so renters always see a
 * useful list.
 *
 * Each route resolves to its own /car-rental/:slug landing page so this
 * widget doubles as an internal link-graph booster (good for SEO).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, MapPin, Plane, Anchor, Building2, TreePalm, TrendingUp } from "lucide-react";

const KIND_ICON = {
  airport: Plane,
  port: Anchor,
  city: Building2,
  tourist: TreePalm,
  hotel_area: Building2,
};

export default function PopularRoutesWidget({ slug, pickupName }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    api.get(`/locations/popular-routes/${encodeURIComponent(slug)}`, {
      params: { limit: 6 },
    })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [slug]);

  if (error || !data || !Array.isArray(data.routes) || data.routes.length === 0) {
    return null;
  }

  return (
    <section
      className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12"
      data-testid="popular-routes-widget"
    >
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold mb-2 inline-flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3" /> Popular routes
          </div>
          <h2 className="font-display text-2xl text-neutral-900">
            Where renters go from {pickupName || data.pickup?.name}
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Tap a destination to see cars + transparent one-way pricing.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="popular-routes-list">
        {data.routes.map((r) => {
          const Icon = KIND_ICON[r.kind] || MapPin;
          return (
            <Link
              key={r.slug}
              to={`/car-rental/${r.slug}`}
              className="group flex items-center gap-3 rounded-2xl bg-white border border-border hover:border-amber-300 hover:shadow-md p-4 transition"
              data-testid={`popular-route-${r.slug}`}
            >
              <span className="h-11 w-11 rounded-2xl bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5 text-amber-700" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-neutral-900 truncate">{r.name}</div>
                <div className="text-xs text-neutral-500 truncate">
                  <span className="tabular-nums font-semibold text-amber-800">{r.km} km</span>
                  <span className="mx-1.5 text-neutral-300">·</span>
                  <span className="capitalize">{(r.kind || "city").replace("_", " ")}</span>
                  {r.hits > 0 && (
                    <>
                      <span className="mx-1.5 text-neutral-300">·</span>
                      <span className="text-emerald-700 font-semibold">{r.hits}× booked</span>
                    </>
                  )}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-neutral-300 group-hover:text-amber-700 transition shrink-0" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}
